/**
 * Forward planning — design/PROTOTYPE-SPEC.md §13, "Plan ahead".
 *
 * A package is an intention: this treatment, made of these products, at this
 * price, this many times, in this month. The arithmetic below is what turns a
 * list of intentions into an answer to "will this month work".
 *
 * No I/O and no ambient time, per CLAUDE.md rules 3 and 4 — the month is always
 * passed in.
 */
import { z } from 'zod';
/** `YYYY-MM`. A month is not a day; storing the 1st would invite timezone maths. */
export const monthSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Month must be YYYY-MM');
export const packageItemSchema = z.object({
    productId: z.string().uuid('Pick a product'),
    qty: z.number().int('Whole units only').min(1, 'At least one unit').max(9999),
});
export const packageSchema = z.object({
    name: z.string().trim().min(1, 'Give the package a name').max(120),
    /** Integer pence, like all money here. */
    pricePence: z.number().int().min(0, 'Price cannot be negative').max(100_000_000),
    plannedCount: z.number().int().min(0, 'Cannot plan a negative number').max(10_000),
    month: monthSchema,
    items: z.array(packageItemSchema).max(50),
});
/** What this package brings in if the plan happens. */
export function plannedRevenuePence(p) {
    return p.pricePence * p.plannedCount;
}
/**
 * What the stock for it costs — the sum of each line's unit cost times how many
 * the recipe uses, times how many treatments are planned.
 *
 * This is why packages carry quantities and treatments do not: without a `qty`
 * you cannot cost a plan, only name it.
 */
export function plannedCostPence(p) {
    const perTreatment = p.items.reduce((sum, i) => sum + i.unitCostPence * i.qty, 0);
    return perTreatment * p.plannedCount;
}
export function plannedProfitPence(p) {
    return plannedRevenuePence(p) - plannedCostPence(p);
}
/**
 * Margin as a percentage, rounded to a whole number.
 *
 * Returns null rather than 0 when there is no revenue: a package priced at zero,
 * or planned zero times, has no margin — and showing "0%" invites someone to
 * treat an undefined number as a bad one.
 */
export function plannedMarginPercent(p) {
    const revenue = plannedRevenuePence(p);
    if (revenue === 0)
        return null;
    return Math.round((plannedProfitPence(p) / revenue) * 100);
}
/** The whole month, added up. */
export function monthPlanTotals(packages) {
    const revenuePence = packages.reduce((s, p) => s + plannedRevenuePence(p), 0);
    const costPence = packages.reduce((s, p) => s + plannedCostPence(p), 0);
    const profitPence = revenuePence - costPence;
    return {
        revenuePence,
        costPence,
        profitPence,
        marginPercent: revenuePence === 0 ? null : Math.round((profitPence / revenuePence) * 100),
        treatments: packages.reduce((s, p) => s + p.plannedCount, 0),
    };
}
/**
 * How much of each product the month's plan will consume.
 *
 * The point of this is the comparison against stock on hand: a plan that needs
 * 40 vials when 6 are on the shelf is a plan with a purchase order hidden in it,
 * and that is worth knowing in September rather than in December.
 */
export function plannedUsage(packages) {
    const out = new Map();
    for (const p of packages) {
        for (const item of p.items) {
            out.set(item.productId, (out.get(item.productId) ?? 0) + item.qty * p.plannedCount);
        }
    }
    return out;
}
/** `2026-09` → `September 2026`. Formatting lives here so the API and SPA agree. */
export function formatMonth(month) {
    const [year, m] = month.split('-');
    const names = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${names[Number(m) - 1] ?? m} ${year}`;
}
/** Steps a `YYYY-MM` by whole months, rolling the year over correctly. */
export function shiftMonth(month, by) {
    const [y, m] = month.split('-').map(Number);
    // Work in zero-based months so the arithmetic is a single modulo rather than
    // a special case at December.
    const total = y * 12 + (m - 1) + by;
    const year = Math.floor(total / 12);
    const mon = total - year * 12 + 1;
    return `${String(year).padStart(4, '0')}-${String(mon).padStart(2, '0')}`;
}
//# sourceMappingURL=packages.js.map